const MAX_FILE_SIZE_MB = 3
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'application/pdf']

export function validateFile(file: File): { valid: boolean; reason?: string } {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, reason: 'File type not allowed.' }
  }

  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return { valid: false, reason: 'File exceeds allowed size.' }
  }

  return { valid: true }
}

// ---------------------------------------------------------------------------
// Filestack upload allowlist (CHG-C1, F4 remediation)
// Mirrors the server allowlist in SERVER/services/fileValidation.js.
// Deny executables / scripts; SVG allowed here — server must serve with
// Content-Disposition: attachment and never inline on the app origin.
// ---------------------------------------------------------------------------

export const ALLOWED_FILE_EXTENSIONS = new Set([
  'pdf',
  'doc', 'docx',
  'ppt', 'pptx',
  'xls', 'xlsx',
  'csv', 'txt',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
  'mp4', 'mov', 'webm',
  'mp3', 'wav',
  'zip',
])

export const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'audio/mpeg',
  'audio/wav',
  'application/zip',
])

/**
 * Returns the lowercase extension of a filename, or '' if none is found.
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.')
  if (lastDot < 0 || lastDot === filename.length - 1) return ''
  return filename.slice(lastDot + 1).toLowerCase()
}

/**
 * Validates a file's extension and MIME type against the Filestack upload
 * allowlist. Both must match; an empty/missing extension is rejected.
 *
 * Returns { allowed: true } when the file passes, or { allowed: false, reason }
 * with a user-facing message when rejected.
 */
export function validateFilestackFile(file: {
  name: string
  type: string
}): { allowed: true } | { allowed: false; reason: string } {
  const ext = getFileExtension(file.name)

  if (!ext) {
    return { allowed: false, reason: `"${file.name}" has no file extension and cannot be uploaded.` }
  }

  if (!ALLOWED_FILE_EXTENSIONS.has(ext)) {
    return { allowed: false, reason: `File type ".${ext}" is not allowed. Please upload documents, images, audio, or video files.` }
  }

  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    return { allowed: false, reason: `The file "${file.name}" has an unrecognised content type ("${file.type}") and cannot be uploaded.` }
  }

  return { allowed: true }
}
